import { useMemo } from "react";
import type { HierarchyNode, HierarchyRectangularNode } from "d3-hierarchy";
import { FilteredLeavesResult } from "../types";
import type { EnrichedTradeDataItem } from "@atlas-int/data/types";
import { UIView } from "@atlas-int/data";

type TreemapLeaf = HierarchyRectangularNode<EnrichedTradeDataItem>;

/** Markets treemap leaves need hybrid metadata for coloring; placeholders omit these fields. */
const hasHybridLevels = (leaf: TreemapLeaf) =>
  Boolean(leaf.data.hybrid_level_1 && leaf.data.hybrid_level_2);

interface UseFilteredLeavesProps {
  root: HierarchyNode<EnrichedTradeDataItem> | undefined;
  servicesRoot: HierarchyNode<EnrichedTradeDataItem> | undefined;
  nonServicesRoot: HierarchyNode<EnrichedTradeDataItem> | undefined;
  view: UIView;
  locationHasServices: boolean | undefined;
}

export const useFilteredLeaves = ({
  root,
  servicesRoot,
  nonServicesRoot,
  view,
  locationHasServices: _locationHasServices,
}: UseFilteredLeavesProps): FilteredLeavesResult => {
  const filteredServicesRootLeaves = useMemo(() => {
    const leaves = (servicesRoot?.leaves() || []) as TreemapLeaf[];
    const filtered =
      view === UIView.Markets ? leaves.filter(hasHybridLevels) : leaves;
    return filtered.length > 0 ? filtered : undefined;
  }, [servicesRoot, view]);

  const filteredNonServicesRootLeaves = useMemo(() => {
    const leaves = (nonServicesRoot?.leaves() || []) as TreemapLeaf[];
    const filtered =
      view === UIView.Markets ? leaves.filter(hasHybridLevels) : leaves;
    return filtered.length > 0 ? filtered : undefined;
  }, [nonServicesRoot, view]);

  const filteredRootLeaves = useMemo(() => {
    if (view === UIView.Markets) {
      // For Markets view, we need hybrid levels for proper nesting
      return (root?.leaves() as TreemapLeaf[] | undefined)?.filter(
        hasHybridLevels,
      );
    } else if (view === UIView.Products) {
      // For Products view, just return all leaves
      // nameEn is not required for rendering, just for labels
      return (root?.leaves() || []) as TreemapLeaf[];
    }
    return [] as TreemapLeaf[];
  }, [root, view]);

  return {
    filteredRootLeaves,
    filteredServicesRootLeaves,
    filteredNonServicesRootLeaves,
  };
};
